import { format, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { parse } from 'date-fns';

import { IUEvent } from './event';


export const convertToUTC = (date: Date, hrs: number, mins: number, timeZone: string) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(hrs).padStart(2, '0');
  const minutes = String(mins).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  const dateStr = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  const utcDate = zonedTimeToUtc(parse(dateStr, 'yyyy-MM-dd HH:mm:ss', new Date()), timeZone);

  return utcDate;
}

const dateFormat = (timeZone?: string) => {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: timeZone || undefined });
}

const timeFormat = (timeZone: string) => {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: 'numeric', hour12: true, timeZone: timeZone || undefined });
}

export const convertUserTime = (timeString: string): number | null => {
  const utcDate = new Date(timeString);
  if (!isNaN(utcDate.getTime())) {
    return utcDate.getTime();
  }
  return null;
};

export const convertTimestampToDate = (timestamp: number, timeZone: string): Date => {
  return utcToZonedTime(timestamp, timeZone);
}

export const convertTimestampToTimeString = (timestamp: number, timeZone: string): string => {
  const date = utcToZonedTime(timestamp, timeZone);
  return format(date, 'yyyy-MM-dd\'T\'HH:mm');
}

export const convertTimestampToDateString = (timestamp: number, timeZone: string): string => {
  const dateTimeFormat = new Intl.DateTimeFormat('default', { weekday: 'long', month: 'short', day: 'numeric' });
  const date = utcToZonedTime(timestamp, timeZone);
  return dateTimeFormat.format(date);
}

export const convertTimestampToShortDateString = (timestamp: number, timeZone: string): string => {
  const dateTimeFormat = new Intl.DateTimeFormat('default', { month: 'short', day: 'numeric' });
  const date = utcToZonedTime(timestamp, timeZone);
  return dateTimeFormat.format(date);
}

export const convertTimestampToDateTimeString = (timestamp: number, timeZone: string): string => {
  const dateString = convertTimestampToDateString(timestamp, timeZone);
  const timeString = generateSingleTimeString(timestamp, timeZone)
  return `${dateString}, ${timeString}`
}

export const convertTimestampToShortDateTimeString = (timestamp: number, timeZone: string): string => {
  const dateString = convertTimestampToShortDateString(timestamp, timeZone);
  const timeString = generateSingleTimeString(timestamp, timeZone)
  return `${dateString}, ${timeString}`
}

const TWELVE_HOURS = 12 * 60 * 60 * 1000;

const hasAmPm = (time: string) => {
  const match = time.match(/([ap])\.?m\.?/i);
  if (match === null) {
    return null;
  }
  return match[1].toUpperCase();
};

export const convertUserTimeRange = (day: Date, time: string, timeZone: string): [number, number] | null => {
  const times = time.match(/^([^-]*[^ ]+) *(?:-|to) *([^-]*[^ ]+)$/);
  if (times !== null) {
    const startTime = parseTime(times[1], false);
    const endTime = parseTime(times[2], true);
    const startAmPm = hasAmPm(times[1]);
    const endAmPm = hasAmPm(times[2]);

    if (startTime !== null && endTime !== null) {
      return [convertToUTC(day, startTime[0], startTime[1], timeZone).getTime(), convertToUTC(day, endTime[0], endTime[1], timeZone).getTime()];
    }
  }

  return null;
};

const amPmHour = (isPm: boolean, hour: number) => {
  if (hour === 12) {
    return isPm ? 12 : 0;
  }
  return hour + (isPm ? 12 : 0);
};

const parseTime = (time: string, isEnd?: boolean) => {
  // e.g. 9 -- ambiguous
  let match = time.match(/^([1-9]?[0-9])$/);
  if (match !== null) {
    return calculateAmbiguousTime(+match[1], 0, isEnd);
  }

  // e.g. 09 -- assume AM because of leading zero
  match = time.match(/^(0[0-9])$/);
  if (match !== null) {
    return [+match[1], 0];
  }

  // e.g. 09:30 -- assume AM because of leading zero
  match = time.match(/^(0[0-9])[:.]([0-9][0-9])$/);
  if (match !== null) {
    return [+match[1], +match[2]];
  }

  // e.g. 11:30 -- ambiguous
  match = time.match(/^([0-9]?[0-9])[:.]([0-9][0-9])$/);
  if (match !== null) {
    return calculateAmbiguousTime(+match[1], +match[2], isEnd);
  }

  // e.g. 09:30pm -- use am/pm in match
  match = time.match(/^([0-9]+)[:.]([0-9][0-9]) *([ap])\.?m?\.?$/i);
  if (match !== null) {
    return [amPmHour(match[3].toLowerCase() === 'p', +match[1]), +match[2]];
  }

  // e.g. 9pm -- use am/pm in match
  match = time.match(/^([0-9]+) *([ap])\.?m?\.?$/i);
  if (match !== null) {
    return [amPmHour(match[2].toLowerCase() === 'p', +match[1]), 0];
  }

  return null;
};

const calculateAmbiguousTime = (hour: number, minute: number, isEnd?: boolean): [number, number] | null => {
  if (isNaN(hour) || isNaN(minute) || hour > 23 || hour < 0 || minute > 59 || minute < 0) {
    return null;
  }

  // Not needed when we use <input type="time"> since it always displays AM/PM to it's never ambiguous
  // if (isEnd === true || hour < 9) {
  //   // All ambiguous end hours and any start hours before 9 should assume to be PM
  //   hour += 12;
  // }

  return [hour, minute];
};

export const dateToUserMidnight = (ts: number): Date => {
  const date = new Date(ts);
  date.setHours(0);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date;
};

export const generateTimeString = (event?: IUEvent) => {
  if (event) {
    if (event.startTime > 0 && event.endTime > 0) {
      return timeFormat(event.timeZone).format(event.startTime) + ' - ' + timeFormat(event.timeZone).format(event.endTime) + ' ' + getShortTimeZone(event.timeZone, event.startTime);
    } else if (event.startTime > 0) {
      return timeFormat(event.timeZone).format(event.startTime) + ' ' + getShortTimeZone(event.timeZone, event.startTime);
    } else return ''
  } else return ''
};

export const generateSingleTimeString = (timestamp: number, timeZone: string) => {
  return timeFormat(timeZone).format(timestamp);
}

export const generateDisplayStartTime = (event?: IUEvent) => {
  if (event && event.startTime > 0) {
    return timeFormat(event.timeZone).format(event.startTime) + ' ' + getShortTimeZone(event.timeZone, event.startTime);
  }
  return '';
}

export const isPast = (event: IUEvent): boolean => {
  const timeZone = event.timeZone

  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0); // Reset to the start of the day
  const todayStartTime = new Date(todayStart.toLocaleString('en-US', { timeZone: timeZone })).getTime()

  // date now - needed for comparison to end time
  const dateNowInUtc = new Date();
  const localDateNow = dateNowInUtc.toLocaleString('en-US', { timeZone: timeZone });
  const dateNow = new Date(localDateNow).getTime();


  //start time
  const eventStartTime = new Date(event.startTime).getTime();
  const localDate = new Date(eventStartTime).toLocaleString('en-US', { timeZone: timeZone });
  const zonedStartTime = new Date(localDate).getTime();

  if (event.endTime !== 0) {
    //end time
    const eventEndTime = new Date(event.endTime).getTime();
    const localDateEnd = new Date(eventEndTime).toLocaleString('en-US', { timeZone: timeZone });
    const zonedEndTime = new Date(localDateEnd).getTime();
    return zonedStartTime < todayStartTime || !!event.cancelledTimestamp || zonedEndTime < dateNow
  }

  return zonedStartTime < todayStartTime || !!event.cancelledTimestamp
}

export const getShortTimeZone = (timeZone: string, ts: number) => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || undefined,
    timeZoneName: 'short'
  }).formatToParts(ts)
    .find(part => part.type == 'timeZoneName')
    ?.value;
}

export const isInvalidEventDuration = (startTime: number, endTime: number) => {
  const TWO_WEEKS_IN_MILLISECONDS = 2 * 7 * 24 * 60 * 60 * 1000;
  return endTime - startTime >= TWO_WEEKS_IN_MILLISECONDS;
}

export const isMultidayEvent = (event: IUEvent) => {
  const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
  if (event.endTime === 0) {
    return false;
  } else {
    return event.endTime - event.startTime >= DAY_IN_MILLISECONDS;
  }
}

export const getDefaultEndTimeString = (startTime: string, timeZone: string) => {
  // Default end time = 3 hours after start time
  const THREE_HOURS_IN_MS = 3 * 60 * 60 * 1000;
  const startTs = convertUserTime(startTime);
  if (startTs !== null) {
    const endTs = startTs + THREE_HOURS_IN_MS;
    return convertTimestampToTimeString(endTs, timeZone)
  } else {
    return '';
  }
}
